// The table pane's popovers (#11833): the column popover a header opens
// (name, type, delete), the choice menu a boolean cell opens, and the reader
// a truncated cell opens in the read view. All are
// the RMS menu — portalled to <body>, placed from the trigger's rect, surface
// ground on a --fp-border hairline at 10px with --fp-lift-overlay, items at
// --fp-t-meta in secondary ink with the --fp-hover wash — the same numbers
// AddSurfaceMenu and ColumnContextMenu paint, so the editor's menus read as
// the RMS grid's.
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Trash2 } from "lucide-react";
import {
  findScrollableAncestor,
  isAnchorOutsideScrollParent,
} from "../FpPages/RMS/anchoredMenu";
import {
  TABLE_COLUMN_TYPES,
  type TableColumn,
  type TableColumnFormat,
  type TableColumnType,
} from "./tableArtifact";

const TYPE_LABELS: Record<TableColumnType, string> = {
  text: "Text",
  number: "Number",
  date: "Date",
  boolean: "Yes / No",
  record: "Record",
  link: "Link",
};

// A number column's display formats, Plain (no format) first.
const FORMAT_CHOICES: {
  format: TableColumnFormat | undefined;
  label: string;
}[] = [
  { format: undefined, label: "Plain" },
  { format: "currency", label: "Currency" },
];

const GAP = 4;
const EDGE = 8;

/** Where a popover hangs from: the trigger's rect, and the trigger itself
 *  so a click on it toggles rather than closing-then-reopening, and so the
 *  popover can follow it when its scroll parent moves. */
export interface PopoverAnchor {
  rect: DOMRect;
  element: HTMLElement;
}

export const anchorFrom = (element: HTMLElement): PopoverAnchor => ({
  rect: element.getBoundingClientRect(),
  element,
});

interface PopoverFrameProps {
  anchor: PopoverAnchor;
  width: number;
  role: "dialog" | "menu";
  "aria-label": string;
  onClose: () => void;
  children: React.ReactNode;
}

// Below the trigger by preference, left-aligned to it; above when below
// cannot hold it (the RMS anchoredMenu rule), never past a window edge.
// Closes on Escape and on a mousedown outside it and its trigger. A scroll
// re-anchors it to the trigger rather than closing: the trigger is a
// focusable button inside the grid's scroll container, so the click that
// opens it can itself scroll the header into view, and a close-on-any-scroll
// rule (ColumnContextMenu's, opened at pointer coordinates with nothing to
// scroll) shut the popover the same frame it opened (review B1). It closes
// once the trigger has scrolled clear of its scroll parent, since there is
// nothing left to point at.
const PopoverFrame = ({
  anchor,
  width,
  role,
  "aria-label": ariaLabel,
  onClose,
  children,
}: PopoverFrameProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState(anchor.rect);
  useEffect(() => {
    setRect(anchor.rect);
  }, [anchor]);
  const [top, setTop] = useState(rect.bottom + GAP);
  useLayoutEffect(() => {
    const height = ref.current?.getBoundingClientRect().height ?? 0;
    const below = rect.bottom + GAP;
    const above = rect.top - GAP - height;
    const fitsBelow = below + height + EDGE <= window.innerHeight;
    setTop(
      fitsBelow || above < EDGE
        ? Math.max(EDGE, Math.min(below, window.innerHeight - EDGE - height))
        : above
    );
  }, [rect]);
  const left = Math.max(
    EDGE,
    Math.min(rect.left, window.innerWidth - width - EDGE)
  );

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || anchor.element.contains(target))
        return;
      onClose();
    };
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      const next = anchor.element.getBoundingClientRect();
      const parent = findScrollableAncestor(anchor.element);
      if (parent && isAnchorOutsideScrollParent(next, parent)) {
        onClose();
        return;
      }
      setRect(next);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [anchor, onClose]);

  return createPortal(
    <div
      ref={ref}
      role={role}
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        // Ours, not the artifact pane's: the pane's document-level Escape
        // already stands down for an open dialog/menu (documentViewerEscape),
        // and stopping propagation here keeps that true.
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
      style={{
        position: "fixed",
        top,
        left,
        width,
        // Above every pane host: in overlay mode the artifact pane is a
        // MUI Drawer (z 1200), so a z-50 popover rendered behind it and the
        // sheet and column popovers looked dead (Gerry, 2026-09-02). The
        // RMS menus portal at 9999; this sits one above, since it can open
        // from inside one of those hosts.
        zIndex: 10000,
        background: "var(--fp-surface)",
        border: "1px solid var(--fp-border)",
        borderRadius: 10,
        padding: 5,
        boxShadow: "var(--fp-lift-overlay)",
        fontFamily: "var(--fp-font-ui)",
        fontSize: "var(--fp-t-meta)",
        color: "var(--fp-ink-2)",
      }}
    >
      {children}
    </div>,
    document.body
  );
};

// The mock's menu item: full-width, 7px radius, the hover wash. Ink on hover
// is the item's own call (the delete row keeps its negative ink).
const itemClass =
  "flex w-full items-center gap-2 rounded-[7px] px-[9px] py-[7px] text-left hover:bg-[var(--fp-hover)] outline-none focus-visible:bg-[var(--fp-hover)]";
const choiceClass = `${itemClass} text-[var(--fp-ink-2)] hover:text-[var(--fp-ink)]`;

const microCapsStyle: React.CSSProperties = {
  fontSize: "var(--fp-t-micro)",
  fontWeight: "var(--fp-w-head)" as unknown as number,
  letterSpacing: "var(--fp-track-caps)",
  color: "var(--fp-ink-4)",
};

interface ColumnPopoverProps {
  anchor: PopoverAnchor;
  column: TableColumn;
  /** A just-added column opens with its placeholder name selected, so
   *  typing replaces it. */
  selectName: boolean;
  onChange: (patch: Partial<TableColumn>) => void;
  onDelete: () => void;
  onClose: () => void;
}

/** A column's schema, edited in place: the name (a DS field), the type
 *  (four check-marked rows), and — below a hairline — Delete column in the
 *  negative ink. Renames apply live; Enter and Escape close. */
export const ColumnPopover = ({
  anchor,
  column,
  selectName,
  onChange,
  onDelete,
  onClose,
}: ColumnPopoverProps) => {
  const nameRef = useRef<HTMLInputElement>(null);
  // Focus follows the trigger: retargeting the popover to another header
  // refocuses the name field for that column.
  useEffect(() => {
    const input = nameRef.current;
    if (!input) return;
    input.focus();
    if (selectName) input.select();
  }, [anchor.element, selectName]);
  return (
    <PopoverFrame
      anchor={anchor}
      width={224}
      role="dialog"
      aria-label={`Column ${column.label || "Column name"}`}
      onClose={onClose}
    >
      <div className="px-1 pb-1.5 pt-1">
        <input
          ref={nameRef}
          aria-label="Column name"
          value={column.label}
          placeholder="Column name"
          onChange={(e) => onChange({ label: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onClose();
            }
          }}
          // The DS field (ColumnSelector's search box): strong hairline, 7px
          // radius, ink-3 focus ring — no accent wash.
          className="h-8 w-full rounded-[7px] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] px-2.5 text-[var(--fp-ink)] outline-none placeholder:text-[var(--fp-ink-4)] focus:border-[var(--fp-ink-3)] focus:ring-1 focus:ring-[var(--fp-ink-3)]"
          style={{ fontSize: "var(--fp-t-meta)" }}
        />
      </div>
      <div role="radiogroup" aria-label="Column type">
        <div className="px-[9px] pb-0.5 pt-1 uppercase" style={microCapsStyle}>
          Type
        </div>
        {TABLE_COLUMN_TYPES.map((type) => {
          const checked = column.type === type;
          return (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={checked}
              // A format belongs to a number column and link text to a link
              // column: another type drops each.
              onClick={() =>
                onChange({
                  type,
                  ...(type === "number" ? {} : { format: undefined }),
                  ...(type === "link" ? {} : { link_text: undefined }),
                })
              }
              className={choiceClass}
              style={checked ? { color: "var(--fp-ink)" } : undefined}
            >
              {TYPE_LABELS[type]}
              <Check
                aria-hidden
                className="ml-auto h-3.5 w-3.5"
                style={{ visibility: checked ? "visible" : "hidden" }}
              />
            </button>
          );
        })}
      </div>
      {column.type === "number" && (
        <div role="radiogroup" aria-label="Column format">
          <div
            className="px-[9px] pb-0.5 pt-1 uppercase"
            style={microCapsStyle}
          >
            Format
          </div>
          {FORMAT_CHOICES.map(({ format, label }) => {
            const checked = column.format === format;
            return (
              <button
                key={label}
                type="button"
                role="radio"
                aria-checked={checked}
                onClick={() => onChange({ format })}
                className={choiceClass}
                style={checked ? { color: "var(--fp-ink)" } : undefined}
              >
                {label}
                <Check
                  aria-hidden
                  className="ml-auto h-3.5 w-3.5"
                  style={{ visibility: checked ? "visible" : "hidden" }}
                />
              </button>
            );
          })}
        </div>
      )}
      {column.type === "link" && (
        <div className="px-1 pb-1.5">
          <div className="px-[5px] pb-1 pt-1 uppercase" style={microCapsStyle}>
            Link text
          </div>
          <input
            aria-label="Link text"
            value={column.link_text ?? ""}
            placeholder="Site name"
            maxLength={60}
            onChange={(e) =>
              onChange({ link_text: e.target.value || undefined })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onClose();
              }
            }}
            className="h-8 w-full rounded-[7px] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] px-2.5 text-[var(--fp-ink)] outline-none placeholder:text-[var(--fp-ink-4)] focus:border-[var(--fp-ink-3)] focus:ring-1 focus:ring-[var(--fp-ink-3)]"
            style={{ fontSize: "var(--fp-t-meta)" }}
          />
        </div>
      )}
      <div className="my-1 h-px" style={{ background: "var(--fp-border)" }} />
      <button
        type="button"
        onClick={onDelete}
        className={itemClass}
        style={{ color: "var(--fp-neg)" }}
      >
        <Trash2 aria-hidden className="h-3.5 w-3.5" />
        Delete column
      </button>
    </PopoverFrame>
  );
};

interface SheetPopoverProps {
  anchor: PopoverAnchor;
  name: string;
  /** False for the workbook's only sheet: the question is not asked. */
  removable: boolean;
  onRename: (name: string) => void;
  onRemove: () => void;
  onClose: () => void;
}

/** A sheet's tab, edited the way a column header is (#11833): the name in
 *  a DS field, and — below a hairline — Delete sheet, when there is more
 *  than one. Renames apply live; Enter and Escape close. */
export const SheetPopover = ({
  anchor,
  name,
  removable,
  onRename,
  onRemove,
  onClose,
}: SheetPopoverProps) => {
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    nameRef.current?.focus();
    nameRef.current?.select();
  }, [anchor.element]);
  return (
    <PopoverFrame
      anchor={anchor}
      width={224}
      role="dialog"
      aria-label={`Sheet ${name || "Sheet name"}`}
      onClose={onClose}
    >
      <div className={`px-1 pt-1 ${removable ? "pb-1.5" : "pb-1"}`}>
        <input
          ref={nameRef}
          aria-label="Sheet name"
          value={name}
          placeholder="Sheet name"
          onChange={(e) => onRename(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onClose();
            }
          }}
          className="h-8 w-full rounded-[7px] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] px-2.5 text-[var(--fp-ink)] outline-none placeholder:text-[var(--fp-ink-4)] focus:border-[var(--fp-ink-3)] focus:ring-1 focus:ring-[var(--fp-ink-3)]"
          style={{ fontSize: "var(--fp-t-meta)" }}
        />
      </div>
      {removable && (
        <>
          <div
            className="my-1 h-px"
            style={{ background: "var(--fp-border)" }}
          />
          <button
            type="button"
            onClick={onRemove}
            className={itemClass}
            style={{ color: "var(--fp-neg)" }}
          >
            <Trash2 aria-hidden className="h-3.5 w-3.5" />
            Delete sheet
          </button>
        </>
      )}
    </PopoverFrame>
  );
};

interface CellTextPopoverProps {
  anchor: PopoverAnchor;
  /** The column's name, which heads the reader and names the dialog. */
  label: string;
  /** The value, rendered as the cell renders it (a cited value stays a
   *  citation). */
  children: React.ReactNode;
  onClose: () => void;
}

const CELL_TEXT_MIN_WIDTH = 320;
const CELL_TEXT_MAX_WIDTH = 480;

/** The whole of a read-only cell whose value its column truncates, anchored
 *  to the cell: the column name in micro-caps, then the value wrapped. A
 *  value taller than the reader scrolls inside it — never clamped, since
 *  clamping the text in the thing opened to read the text is the defect it
 *  exists to fix. Escape and an outside click close it. */
export const CellTextPopover = ({
  anchor,
  label,
  children,
  onClose,
}: CellTextPopoverProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  // Focus inside, so Escape reaches the frame; no scroll to it, since the
  // reader is fixed and its cell is already where the user is looking. The
  // host keys the reader by cell, so this runs once per cell.
  useEffect(() => {
    bodyRef.current?.focus({ preventScroll: true });
  }, []);
  // The overflow cue for the quiet scroller (rules/frontend.md, Scrollbars):
  // a bottom fade shown only while text continues below the fold.
  const [more, setMore] = useState(false);
  const updateMore = () => {
    const body = bodyRef.current;
    if (body)
      setMore(body.scrollTop + body.clientHeight < body.scrollHeight - 1);
  };
  useLayoutEffect(updateMore, [children]);
  const width = Math.min(
    Math.max(anchor.rect.width, CELL_TEXT_MIN_WIDTH),
    CELL_TEXT_MAX_WIDTH,
    window.innerWidth - 2 * EDGE
  );
  return (
    <PopoverFrame
      anchor={anchor}
      width={width}
      role="dialog"
      aria-label={label}
      onClose={onClose}
    >
      <div className="px-[9px] pb-1 pt-1.5 uppercase" style={microCapsStyle}>
        {label}
      </div>
      <div className="relative">
        <div
          ref={bodyRef}
          tabIndex={-1}
          onScroll={updateMore}
          className="fp-scroll-quiet overflow-y-auto px-[9px] pb-2 pt-0.5 outline-none"
          style={{
            maxHeight: "min(60vh, 420px)",
            fontSize: "var(--fp-t-body)",
            lineHeight: 1.5,
            color: "var(--fp-ink)",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {children}
        </div>
        {more && (
          <div
            aria-hidden
            data-more-fade=""
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8"
            style={{
              background:
                "linear-gradient(to bottom, transparent, var(--fp-surface))",
            }}
          />
        )}
      </div>
    </PopoverFrame>
  );
};

interface Choice {
  value: string;
  label: string;
}

interface ChoiceMenuProps {
  anchor: PopoverAnchor;
  "aria-label": string;
  choices: Choice[];
  value: string;
  onPick: (value: string) => void;
  onClose: () => void;
}

/** One value from a short list, anchored to the cell that holds it: the
 *  boolean cell's —, Yes, No. The current value carries the check. */
export const ChoiceMenu = ({
  anchor,
  "aria-label": ariaLabel,
  choices,
  value,
  onPick,
  onClose,
}: ChoiceMenuProps) => {
  const currentRef = useRef<HTMLButtonElement>(null);
  const firstRef = useRef<HTMLButtonElement>(null);
  // Focus lands inside the menu whatever the cell holds: a stored value no
  // choice matches (the validator admits any cell) has no checked row, and
  // Escape only reaches the menu from inside it (final review M2).
  useEffect(() => {
    (currentRef.current ?? firstRef.current)?.focus();
  }, []);
  return (
    <PopoverFrame
      anchor={anchor}
      width={Math.max(anchor.rect.width, 160)}
      role="menu"
      aria-label={ariaLabel}
      onClose={onClose}
    >
      {choices.map((choice, index) => {
        const checked = choice.value === value;
        return (
          <button
            key={choice.value}
            ref={checked ? currentRef : index === 0 ? firstRef : undefined}
            type="button"
            role="menuitemradio"
            aria-checked={checked}
            onClick={() => {
              onPick(choice.value);
              onClose();
            }}
            className={choiceClass}
            style={checked ? { color: "var(--fp-ink)" } : undefined}
          >
            {choice.label}
            <Check
              aria-hidden
              className="ml-auto h-3.5 w-3.5"
              style={{ visibility: checked ? "visible" : "hidden" }}
            />
          </button>
        );
      })}
    </PopoverFrame>
  );
};
